"use client";

import React, { useCallback, useState } from "react";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/shadcn-components/ui/resizable";
import ChartScreen from "@/components/chart/ChartScreen";
import { ChartPanelProvider } from "@/components/chart/ChartPanelContext";
import { useDesktopLayoutStore } from "@/lib/desktop-layout/store";
import { DESKTOP_LAYOUT_STORAGE_KEYS } from "@/lib/localStorageKeys/desktop-layout";
import { cn } from "@/lib/utils";
import type { ChartPanelId } from "@/vybe-trader-client/chart/stores/chartPanelTypes";

const MIN_CHART_SPLIT = 20;

function clampPct(n: number): number {
  return Math.min(Math.max(n, MIN_CHART_SPLIT), 100 - MIN_CHART_SPLIT);
}

function readPct(key: string, fallback: number): number {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const n = parseFloat(raw);
    if (!Number.isFinite(n)) return fallback;
    return clampPct(n);
  } catch {
    return fallback;
  }
}

function writePct(key: string, pct: number) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, String(clampPct(pct)));
  } catch {
    /* ignore */
  }
}

function usePersistedSplit(storageKey: string, fallback = 50) {
  const [initialPercent] = useState(() => readPct(storageKey, fallback));

  const handleLayout = useCallback(
    (sizes: number[]) => {
      if (sizes.length >= 1 && sizes[0] != null) {
        writePct(storageKey, sizes[0]);
      }
    },
    [storageKey],
  );

  return { initialPercent, handleLayout };
}

function ChartPanelShell({
  panelId,
  children,
}: {
  panelId: ChartPanelId;
  children: React.ReactNode;
}) {
  const focused = useDesktopLayoutStore((s) => s.focusedChartPanelId);
  const isFocused = focused === panelId;

  const assignFocus = () => {
    const s = useDesktopLayoutStore.getState();
    if (s.focusedChartPanelId !== panelId) {
      s.setFocusedChartPanelId(panelId);
    }
  };

  return (
    <div
      className="relative flex min-h-0 min-w-0 w-full flex-1 flex-col overflow-hidden rounded-md outline-none"
      /* Bubble phase (not capture) so Radix PopoverTrigger opens first; defer focus for the same reason */
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        queueMicrotask(() => assignFocus());
      }}
    >
      {children}
      {/* Overlay above chart content — inset glow on parent would sit under opaque children */}
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-0 rounded-md transition-[box-shadow] duration-300 ease-out",
          isFocused &&
            "shadow-[inset_0_0_0_1px_hsl(var(--primary)/0.28),inset_0_0_10px_1px_hsl(var(--primary)/0.07),inset_0_0_32px_6px_hsl(var(--primary)/0.04)]",
        )}
      />
    </div>
  );
}

export default function DesktopChartArea() {
  const chartLayoutMode = useDesktopLayoutStore((s) => s.chartLayoutMode);
  const focusedChartPanelId = useDesktopLayoutStore(
    (s) => s.focusedChartPanelId,
  );

  const h2 = usePersistedSplit(
    DESKTOP_LAYOUT_STORAGE_KEYS.CHART_H2_LEFT_PCT,
    50,
  );
  const v2 = usePersistedSplit(DESKTOP_LAYOUT_STORAGE_KEYS.CHART_V2_TOP_PCT, 50);
  const quadRow = usePersistedSplit(
    DESKTOP_LAYOUT_STORAGE_KEYS.CHART_QUAD_ROW_TOP_PCT,
    50,
  );
  const quadTopCol = usePersistedSplit(
    DESKTOP_LAYOUT_STORAGE_KEYS.CHART_QUAD_TOP_COL_LEFT_PCT,
    50,
  );
  const quadBotCol = usePersistedSplit(
    DESKTOP_LAYOUT_STORAGE_KEYS.CHART_QUAD_BOTTOM_COL_LEFT_PCT,
    50,
  );

  const panelClass = "flex min-h-0 min-w-0 flex-col overflow-hidden";

  const renderScreen = (id: ChartPanelId) => (
    <ChartPanelShell panelId={id}>
      <ChartPanelProvider panelId={id}>
        <ChartScreen
          isDesktop
          ownsMarketDepthSubscription={focusedChartPanelId === id}
        />
      </ChartPanelProvider>
    </ChartPanelShell>
  );

  if (chartLayoutMode === "single") {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {renderScreen(0)}
      </div>
    );
  }

  if (chartLayoutMode === "two-horizontal") {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <ResizablePanelGroup
          direction="horizontal"
          className="h-full min-h-0 flex-1"
          onLayout={h2.handleLayout}
        >
          <ResizablePanel
            className={panelClass}
            defaultSize={h2.initialPercent}
            minSize={MIN_CHART_SPLIT}
          >
            {renderScreen(0)}
          </ResizablePanel>
          <ResizableHandle />
          <ResizablePanel
            className={panelClass}
            defaultSize={100 - h2.initialPercent}
            minSize={MIN_CHART_SPLIT}
          >
            {renderScreen(1)}
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    );
  }

  if (chartLayoutMode === "two-vertical") {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <ResizablePanelGroup
          direction="vertical"
          className="h-full min-h-0 flex-1"
          onLayout={v2.handleLayout}
        >
          <ResizablePanel
            className={panelClass}
            defaultSize={v2.initialPercent}
            minSize={MIN_CHART_SPLIT}
          >
            {renderScreen(0)}
          </ResizablePanel>
          <ResizableHandle />
          <ResizablePanel
            className={panelClass}
            defaultSize={100 - v2.initialPercent}
            minSize={MIN_CHART_SPLIT}
          >
            {renderScreen(1)}
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      <ResizablePanelGroup
        direction="vertical"
        className="h-full min-h-0 flex-1"
        onLayout={quadRow.handleLayout}
      >
        <ResizablePanel
          className={panelClass}
          defaultSize={quadRow.initialPercent}
          minSize={MIN_CHART_SPLIT}
        >
          <ResizablePanelGroup
            direction="horizontal"
            className="h-full min-h-0 min-w-0 flex-1"
            onLayout={quadTopCol.handleLayout}
          >
            <ResizablePanel
              className={panelClass}
              defaultSize={quadTopCol.initialPercent}
              minSize={MIN_CHART_SPLIT}
            >
              {renderScreen(0)}
            </ResizablePanel>
            <ResizableHandle />
            <ResizablePanel
              className={panelClass}
              defaultSize={100 - quadTopCol.initialPercent}
              minSize={MIN_CHART_SPLIT}
            >
              {renderScreen(1)}
            </ResizablePanel>
          </ResizablePanelGroup>
        </ResizablePanel>
        <ResizableHandle />
        <ResizablePanel
          className={panelClass}
          defaultSize={100 - quadRow.initialPercent}
          minSize={MIN_CHART_SPLIT}
        >
          <ResizablePanelGroup
            direction="horizontal"
            className="h-full min-h-0 min-w-0 flex-1"
            onLayout={quadBotCol.handleLayout}
          >
            <ResizablePanel
              className={panelClass}
              defaultSize={quadBotCol.initialPercent}
              minSize={MIN_CHART_SPLIT}
            >
              {renderScreen(2)}
            </ResizablePanel>
            <ResizableHandle />
            <ResizablePanel
              className={panelClass}
              defaultSize={100 - quadBotCol.initialPercent}
              minSize={MIN_CHART_SPLIT}
            >
              {renderScreen(3)}
            </ResizablePanel>
          </ResizablePanelGroup>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}
